import { FormField, type FormFieldProps } from "@repo/skapa";
import { SearchField } from "@repo/ui";
import { type ChangeEvent, useState } from "react";

import { useDebouncedEffect } from "../hooks/useDebouncedEffect";

export interface DebouncedSearchFieldProps extends Omit<
  FormFieldProps,
  "onChange"
> {
  dataTestId?: string;
  placeholder: string;
  autoFocus?: boolean;
  value?: string;
  debounceTime?: number;
  onChange: (term: string) => void;
  onClear?: () => void;
  id: string;
}

export const DebouncedSearchField = ({
  placeholder,
  value,
  debounceTime = 500,
  dataTestId,
  onChange,
  onClear,
  id,
  ...props
}: DebouncedSearchFieldProps) => {
  const [searchValue, setSearchValue] = useState<string | undefined>(value);
  useDebouncedEffect(
    () => {
      if (searchValue !== undefined) {
        onChange(searchValue);
      }
    },
    debounceTime,
    [searchValue]
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  };

  const handleClear = () => {
    setSearchValue("");
    if (onClear) {
      onClear();
    }
  };

  return (
    <FormField {...props}>
      <SearchField
        id={id}
        placeholder={placeholder}
        data-testid={dataTestId ?? "search-field"}
        aria-label="search-field"
        onClear={handleClear}
        onChange={(e) => {
          handleChange(e);
        }}
        value={searchValue ?? ""}
      />
    </FormField>
  );
};
